import React from "react";
import { formatDate } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface PrintLayoutTemplateProps {
  data?: any;
  config?: any;
}

export default function PrintLayoutTemplate({
  data = {},
  config,
}: PrintLayoutTemplateProps) {
  const c = getCompanyConfig(config);
  const docId = data?.code || data?.id || "";

  return (
    <div
      className="kh-print-doc bg-white w-full mx-auto"
      style={{
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {/* 1. Header & ISO Badge */}
      <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4 mb-4">
        {/* Logo & Info */}
        <div className="flex items-center gap-4 flex-1">
          {c.logoUrl && (
            <img src={c.logoUrl} alt="Logo" className="w-16 h-16 object-contain print:block" />
          )}
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase">
              {c.companyName}
            </h2>
            <p className="text-[10pt] text-slate-600">
              {c.address1 || c.address2}
            </p>
          </div>
        </div>
      </div>

      {/* 2. Title (CENTERED, NO QR) */}
      <PrintDocumentTitle
        title="SƠ ĐỒ BÀI BÌNH IN"
        subtitle="PRINT IMPOSITION LAYOUT"
        docNo={data.code || docId}
        date={data.createdAt || new Date()}
        dateLabel="Ngày lập"
      />

      {/* 3. General Information (BORDERLESS 2-COL TABS) */}
      <div className="mb-6">
        <PrintInfoGrid
          labelWidth="w-[130px]"
          leftColumn={[
            {
              label: "Tên sản phẩm",
              value: data.productName || data.name || "—",
              isBold: true,
            },
            {
              label: "Khổ giấy in",
              value: data.paperSizeX && data.paperSizeY ? `${data.paperSizeX} x ${data.paperSizeY} cm` : "—",
            },
            {
              label: "Khổ thành phẩm",
              value: data.finalSizeX && data.finalSizeY ? `${data.finalSizeX} x ${data.finalSizeY} cm` : "—",
            },
            {
              label: "Ghi chú giấy",
              value: data.paperType || "—",
            },
          ]}
          rightColumn={[
            {
              label: "Số con / tờ in",
              value: data.ups ? `${data.ups} con (Ups)` : "—",
              isBold: true,
              highlight: true,
            },
            {
              label: "Bù nhíp (Gripper)",
              value: data.gripperMargin ? `${data.gripperMargin} cm` : "—",
            },
            {
              label: "Bleed (Xén)",
              value: data.bleedMargin ? `${data.bleedMargin} mm` : "—",
            },
            {
              label: "Máy in chỉ định",
              value: data.machineCompat ? (Array.isArray(data.machineCompat) ? data.machineCompat.join(", ") : data.machineCompat) : "—",
              isBold: true,
            },
          ]}
        />
      </div>

      {/* Ghi chú của bài bình (nhập ở form bình file) — trước đây không in ra */}
      <div className="mb-4 text-[10pt] print:break-inside-avoid" data-testid="print-layout-notes">
        <span className="font-bold">Ghi chú:</span>{" "}
        <span className="whitespace-pre-wrap">{data.notes || "..................................................................................."}</span>
      </div>

      {/* 4. Wireframe Placeholder for Print Layout Diagram */}
      <div className="mb-6 print:break-inside-avoid">
        <h3 className="font-bold text-[11pt] mb-2 uppercase">Sơ đồ bế / Cắt (Wireframe)</h3>
        <div className="w-full h-[400px] border-2 border-slate-400 border-dashed bg-slate-50 flex items-center justify-center relative">
          <div className="text-slate-400 text-center">
            <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <p className="font-semibold">(Dán sơ đồ bình trang cắt / bế vào đây)</p>
            <p className="text-sm">Yêu cầu ghi chú rõ vị trí đầu nhíp (Gripper), tay kê (Side lay)</p>
          </div>
          {/* Mock layout metrics guides */}
          <div className="absolute top-4 left-4 text-xs text-slate-500 font-mono">Đầu nhíp &#8595;</div>
          <div className="absolute top-1/2 left-2 text-xs text-slate-500 font-mono -rotate-90">Tay kê &#8594;</div>
        </div>
      </div>

      {/* 5. Signatures */}
      <div className="grid grid-cols-4 gap-4 text-center mt-8 print:break-inside-avoid text-[10pt]">
        <div>
          <div className="font-bold">LẬP BIỂU (THIẾT KẾ)</div>
          <div className="italic text-slate-500 text-xs mb-16">(Ký & ghi rõ họ tên)</div>
          <div>___________________</div>
        </div>
        <div>
          <div className="font-bold">KCS / QC</div>
          <div className="italic text-slate-500 text-xs mb-16">(Ký & ghi rõ họ tên)</div>
          <div>___________________</div>
        </div>
        <div>
          <div className="font-bold">THỢ IN / THỢ CẮT</div>
          <div className="italic text-slate-500 text-xs mb-16">(Ký nhận sơ đồ)</div>
          <div>___________________</div>
        </div>
        <div>
          <div className="font-bold">TRƯỞNG PHÒNG KT</div>
          <div className="italic text-slate-500 text-xs mb-16">(Ký & ghi rõ họ tên)</div>
          <div>___________________</div>
        </div>
      </div>
    </div>
  );
}
